<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { storeOrdersApiClient } from '@api-client/store_orders';
import type { ResType } from '@api-client/typeUtils';
import { toast } from '@/ui-plus/sonner';
import { formatCurrency } from '@lib/utils/formatCurrency';
import { formatDate } from '@lib/utils/formatDate';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-vue-next';

const historyApiPath = storeOrdersApiClient.customers.credit_history[':storeId'].$post;
type CreditHistoryItem = ResType<typeof historyApiPath>["data"]["items"][0];

const props = defineProps<{
    storeId: string;
    userId: string;
}>();

const historyItems = ref<CreditHistoryItem[]>([]);
const isHistoryLoading = ref(false);
const hasNextPage = ref(false);
const currentCursor = ref<{ created_at_ts: number; id: string } | null>(null);
const cursors = ref<Array<{ created_at_ts: number; id: string } | null>>([null]);
const currentPageIndex = ref(0);

const loadHistory = async (cursor?: { created_at_ts: number; id: string } | null) => {
    isHistoryLoading.value = true;
    try {
        const res = await (await storeOrdersApiClient.customers.credit_history[':storeId'].$post({
            param: { storeId: props.storeId },
            json: {
                userId: props.userId,
                cursor: cursor || undefined,
                pageSize: 10,
            },
        })).json();
        if (res.error) {
            toast.error(res.error.message || 'Failed to load credit history');
            return;
        }
        historyItems.value = res.data.items || [];
        hasNextPage.value = res.data.hasNextPage;
        currentCursor.value = res.data.nextCursor;
    } catch (e) {
        console.error(e);
        toast.error('Failed to load credit history');
    } finally {
        isHistoryLoading.value = false;
    }
};

const loadFromStart = async () => {
    currentPageIndex.value = 0;
    cursors.value = [null];
    currentCursor.value = null;
    await loadHistory(null);
};

const goToNextPage = () => {
    if (hasNextPage.value && currentCursor.value) {
        cursors.value.push(currentCursor.value);
        currentPageIndex.value++;
        loadHistory(currentCursor.value);
    }
};

const goToPrevPage = () => {
    if (currentPageIndex.value > 0) {
        cursors.value.pop();
        currentPageIndex.value--;
        const prevCursor = cursors.value[cursors.value.length - 1];
        loadHistory(prevCursor);
    }
};

onMounted(() => {
    loadFromStart();
});
</script>

<template>
    <div class="mt-4">
        <div v-if="isHistoryLoading && historyItems.length === 0"
             class="flex justify-center items-center py-8">
            <Loader2 class="h-6 w-6 animate-spin" />
            <span class="ml-2">Loading history...</span>
        </div>
        <div v-else-if="!historyItems || historyItems.length === 0"
             class="text-center py-8 text-muted-foreground">
            No credit history found
        </div>
        <div v-else
             class="space-y-3">
            <div v-for="item in historyItems"
                 :key="item.userCreditBalanceHistoryId"
                 class="p-3 border rounded-lg">
                <div class="flex flex-wrap items-start justify-between gap-3">
                    <div class="space-y-1">
                        <div class="text-xs text-muted-foreground">Amount</div>
                        <div :class="[
                            'font-semibold',
                            item.amount > 0 ? 'text-green-600' :
                                item.amount < 0 ? 'text-red-600' : 'text-muted-foreground'
                        ]">
                            {{ formatCurrency(item.amount || 0, item.currency || 'GBP') }}
                        </div>
                    </div>
                    <div class="space-y-1 text-right">
                        <div class="text-xs text-muted-foreground">Ending Balance</div>
                        <div class="font-semibold">
                            {{ formatCurrency(item.endingBalance || 0, item.currency || 'GBP') }}
                        </div>
                    </div>
                </div>
                <div class="flex flex-wrap items-center gap-2 mt-2">
                    <Badge variant="outline"
                           class="text-xs">
                        {{ item.reason }}
                    </Badge>
                    <Badge variant="outline"
                           class="text-xs">
                        {{ item.currency }}
                    </Badge>
                    <Badge v-if="item.storeOrderId"
                           variant="outline"
                           class="text-xs">
                        {{ item.storeOrderId }}
                    </Badge>
                </div>
                <div class="text-xs mt-1">
                    {{ formatDate(item.created_at_ts, undefined, true) }}
                </div>
            </div>
            <div class="flex items-center justify-between pt-2">
                <div class="text-sm text-muted-foreground">
                    Page {{ currentPageIndex + 1 }}
                </div>
                <div class="flex gap-2">
                    <Button @click="goToPrevPage"
                            :disabled="currentPageIndex === 0 || isHistoryLoading"
                            variant="outline"
                            size="sm">
                        <ChevronLeft class="h-4 w-4 mr-1" />
                        Previous
                    </Button>
                    <Button @click="goToNextPage"
                            :disabled="!hasNextPage || isHistoryLoading"
                            variant="outline"
                            size="sm">
                        Next
                        <ChevronRight class="h-4 w-4 ml-1" />
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>
